import { afterNextRender, Component, computed, DestroyRef, effect, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { CachedProjectService } from '../../../../shared/services/cached-project.service';
import { debounceTime, catchError, EMPTY } from 'rxjs';
import * as d3 from 'd3';
import { rxResource, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { ShotType } from '../../../../models/shotType';


@Component({
  selector: 'app-wiggle-plotting',
  templateUrl: './wiggle-plotting.component.html',
  styleUrl: './wiggle-plotting.component.less',
  standalone: false,
})
export class WigglePlottingComponent {
  private graph = viewChild.required<ElementRef>("graph");
  private container = viewChild.required<ElementRef>("graphContainer");

  protected shotData = input<any>()
  protected selectedShotId = input<string>()
  protected iteration = input<number>()
  protected fwd = input<number>()
  protected height = input.required<number>()
  protected panel = input<"Prediction" | "Field">("Prediction")
  protected startTrace = input<number>()
  protected endTrace = input<number>()
  protected startTime = input<number>()
  protected endTime = input<number>()
  // [-max, max] amplitude clip range this panel is scaled to (same range used for the image's color scale)
  protected ampScaling = input<number[]>()

  protected shotType = input<ShotType>()

  protected wiggleForm = input<FormGroup>();

  // avoids clip-path id collisions if more than one wiggle plot is ever rendered at once
  private readonly instanceId = Math.random().toString(36).slice(2);

  private readonly containerSize = signal<{ width: number; height: number } | null>(null);
  private resizeTick = toSignal(
    toObservable(this.containerSize).pipe(debounceTime(100))
  )
  private resizeObserver?: ResizeObserver;

  // snapshot of the wiggleForm's `gap` control, updated only on submit (see submitForm());
  // as a real signal, wiggleDataSource's params() picks up changes to it on its own
  private submittedGap = signal<number | undefined>(undefined);

  private submittedTraceRange = signal<{
    startTrace?: number;
    endTrace?: number;
    startTime?: number;
    endTime?: number;
  }>({});

  // d3 variables
  private svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private xScale: any;
  private yScale: any;
  private xAxis: any;
  private yAxis: any;

  private padding: { left: number; right: number; top: number; bottom: number } = {
    left: 55,
    right: 30,
    top: 10,
    bottom: 40,
  };
  protected width: any;

  protected currentJobResource = rxResource({
    stream: () => this.cachedProjectService.currentJob,
  });

  protected jobId = computed(() => this.currentJobResource.value()?.id);

  constructor(
    private cachedProjectService: CachedProjectService,
    private notificationService: NzNotificationService,
  ) {
    afterNextRender(() => {
      const el = this.container().nativeElement;
      this.resizeObserver = new ResizeObserver((entries) => {
        const rect = entries[0]?.contentRect;
        if (rect && rect.width > 0) {
          this.containerSize.set({ width: rect.width, height: rect.height });
        }
      });
      this.resizeObserver.observe(el);
      this.containerSize.set({ width: el.clientWidth, height: el.clientHeight });
    });
    inject(DestroyRef).onDestroy(() => this.resizeObserver?.disconnect());

    effect(() => {
      this.resizeTick();
      this.ampScaling();
      const traces = this.wiggleDataSource.value()
      if (traces?.length) {
        this.plotWiggleTrace(traces)
      }
    })
  }

  ngOnInit() {
    this.submittedGap.set(this.wiggleForm()!.getRawValue().gap);
    this.submittedTraceRange.set({
      startTrace: this.startTrace(),
      endTrace: this.endTrace(),
      startTime: this.startTime(),
      endTime: this.endTime(),
    });
  }

  submitTraceRange(
    startTrace?: number,
    endTrace?: number,
    startTime?: number,
    endTime?: number,
  ) {
    this.submittedTraceRange.set({ startTrace, endTrace, startTime, endTime });
  }

  protected wiggleDataSource = rxResource({
    params: () => {
      const traceRange = this.submittedTraceRange();
      return {
        jobId: this.jobId(),
        shotId: this.selectedShotId(),
        type: this.shotType()?.ttrName,
        iteration: this.iteration(),
        gap: this.submittedGap(),
        panel: this.panel(),
        fwd: this.fwd(),
        startTrace: traceRange.startTrace,
        endTrace: traceRange.endTrace,
        startTime: traceRange.startTime,
        endTime: traceRange.endTime,
      };
    },
    stream: ({params}) => {
      if (!params.jobId || !params.shotId || params.gap == null) return EMPTY
      const panelIndex = params.panel == "Prediction" ? 0 : params.panel == "Field" ? 1 : 0

      const queryParams: { [key: string]: string | number } = {
        type: params.type!,
        shot_id: params.shotId!,
        iteration: params.iteration!,
        gap: params.gap,
        panel: panelIndex,
        fwd: params.fwd!,
        startTrace: params.startTrace!,
        endTrace: params.endTrace!,
        startTime: params.startTime!,
        endTime: params.endTime!,
      };

      return this.cachedProjectService.getWiggleSourceData(params.jobId, queryParams).pipe(
        catchError((err) => {
          this.notificationService.error(
            "Error",
            err.error?.error || "There was an unexpected error.",
          );
          return EMPTY
        })
      )
    }
  })

  submitForm() {
    this.submittedGap.set(this.wiggleForm()!.getRawValue().gap);

    const traces = this.wiggleDataSource.value();
    if (traces?.length) {
      this.plotWiggleTrace(traces);
    }
  }

  plotWiggleTrace(traces: { trace_num: number; trace: [ time: number, value: number ][] }[]) {
    if (!traces.length) return;

    traces = [...traces].sort((a, b) => a.trace_num - b.trace_num);

    d3.select(this.graph()!.nativeElement).selectAll("*").remove();

    const containerWidth = this.container().nativeElement.offsetWidth;
    this.width = containerWidth;

    this.svg = d3
      .select(this.graph()!.nativeElement)
      .attr("width", containerWidth)
      .attr("height", this.height() + this.padding.top + this.padding.bottom);

    const minTime = this.startTime() || 0;

    // time runs top-to-bottom
    const maxTime =
      this.endTime() ||
      Math.max(...traces.map((t) => t.trace[t.trace.length - 1][0]));

    this.yScale = d3
      .scaleLinear()
      .domain([minTime, maxTime])
      .range([this.padding.top, this.height() + this.padding.top]);
    this.yAxis = d3.axisLeft(this.yScale);

    // one column per trace, laid out left-to-right by trace_num
    this.xScale = d3
      .scaleLinear()
      .domain([0, traces.length])
      .range([this.padding.left, containerWidth - this.padding.right]);
    this.xAxis = d3
      .axisBottom(this.xScale)
      .tickValues(traces.map((_, i) => i + 0.5))
      .tickFormat((_, i) => `${traces[i].trace_num}`);

    // amplitude is scaled to fit within a trace's own column, offset from its baseline.
    const traceSpacing = this.xScale(1) - this.xScale(0);
    const { negFill, clip } = this.wiggleForm()!.getRawValue();
    // map to the panel's given clip range (same range the image color scale uses) rather than
    // auto-fitting to whatever traces happen to be visible, so the wiggle amplitude is stable
    // and comparable across redraws/pages.
    const givenLimit = this.ampScaling()?.[1];
    const amplitudeLimit =
      givenLimit != null
        ? givenLimit
        : Math.max(
            ...traces.flatMap((t) => t.trace.map((d) => Math.abs(d[1]))),
          ) * 1.1;
    const amplitudeScale = d3
      .scaleLinear()
      .domain([-amplitudeLimit, amplitudeLimit])
      .range([-traceSpacing / 2, traceSpacing / 2]);

    // fixed pixel gap kept between adjacent trace columns, independent of scaling
    const traceGapPx = 4;

    // the given (un-gained) clip range, so a trace visibly poking past these guides
    // means it's clipping in the underlying image too, regardless of the gain slider
    const givenMin = this.ampScaling()?.[0];
    const givenMax = this.ampScaling()?.[1];
    const hasGivenRange = givenMin != null && givenMax != null;

    traces.forEach((traceData, i) => {
      const baseX = this.xScale(i) + traceSpacing / 2;

      // negative excursions (left of the trace's baseline) fill blue, positive (right) fill red
      const negativeArea = d3
        .area<[time: number, value: number]>()
        .x0(baseX)
        .x1((d) => baseX + amplitudeScale(Math.min(0, d[1])))
        .y((d) => this.yScale(d[0]));

      const positiveArea = d3
        .area<[time: number, value: number]>()
        .x0(baseX)
        .x1((d) => baseX + amplitudeScale(Math.max(0, d[1])))
        .y((d) => this.yScale(d[0]));

      const wiggleLine = d3
        .line<[time: number, value: number]>()
        .x((d) => baseX + amplitudeScale(d[1]))
        .y((d) => this.yScale(d[0]));

      // clip this trace to its own column (minus the gap) so high scaling cuts off
      // instead of bleeding into neighboring traces
      const clipId = `wiggle-clip-${this.instanceId}-${i}`;
      if (clip) {
        this.svg
          .append("clipPath")
          .attr("id", clipId)
          .append("rect")
          .attr("x", this.xScale(i) + traceGapPx / 2)
          .attr("y", this.padding.top)
          .attr("width", Math.max(0, traceSpacing - traceGapPx))
          .attr("height", this.height);
      }

      const traceGroup = this.svg.append("g");
      if (clip) {
        traceGroup.attr("clip-path", `url(#${clipId})`);
      }

      traceGroup
        .append("path")
        .datum(traceData.trace)
        .attr("d", negativeArea)
        .attr("fill", "blue")
        .attr("fill-opacity", negFill ? 1 : 0);

      traceGroup
        .append("path")
        .datum(traceData.trace)
        .attr("d", positiveArea)
        .attr("fill", "red")
        .attr("fill-opacity", 1);

      traceGroup
        .append("path")
        .datum(traceData.trace)
        .attr("d", wiggleLine)
        .attr("fill", "none")
        .attr("stroke", "#111")
        .attr("stroke-width", 1);

      if (hasGivenRange) {
        // drawn on the unclipped svg (not traceGroup) so they stay visible even when
        // gain pushes them past the column's own clip path
        [givenMin, givenMax].forEach((amplitude) => {
          const x = baseX + amplitudeScale(amplitude);
          this.svg
            .append("line")
            .attr("x1", x)
            .attr("x2", x)
            .attr("y1", this.padding.top)
            .attr("y2", this.height() + this.padding.top)
            .attr("stroke", "#999")
            .attr("stroke-dasharray", "3,3")
            .attr("stroke-width", 1);
        });
      }
    });

    this.svg
      .append("g")
      .attr("class", "y-axis")
      .attr("transform", `translate(${this.padding.left},0)`)
      .call(this.yAxis);

    this.svg
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0,${this.height() + this.padding.top})`)
      .call(this.xAxis);

    this.svg
      .append("text")
      .attr("class", "x-axis-label")
      .attr("text-anchor", "middle")
      .attr(
        "x",
        this.padding.left +
          (containerWidth - this.padding.left - this.padding.right) / 2,
      )
      .attr("y", this.height() + this.padding.top + this.padding.bottom - 5)
      .text("Trace");

    this.svg
      .append("text")
      .attr("class", "y-axis-label")
      .attr("text-anchor", "middle")
      .attr("transform", "rotate(-90)")
      .attr("x", -(this.padding.top + this.height() / 2))
      .attr("y", 15)
      .text("Time (s)");
  }
}
